import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useList } from "@refinedev/core";
import { VaultSidebar, VaultDocItem } from "@/components/digibox/VaultSidebar";

function mapPolicyStatus(
  policystatus?: string
): VaultDocItem["status"] {
  if (policystatus === "Active") return "action";
  if (policystatus === "Archived") return "accepted";
  return "read";
}

type StatusFilter = "all" | "Active" | "Draft" | "Archived";

const FILTER_OPTIONS: Array<{ key: StatusFilter; label: string }> = [
  { key: "all", label: "All" },
  { key: "Active", label: "Active" },
  { key: "Draft", label: "Draft" },
  { key: "Archived", label: "Archived" },
];

export const PolicyList: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [search, setSearch] = useState("");

  const filters: any[] = [];
  if (statusFilter !== "all") {
    filters.push({
      field: "policystatus",
      operator: "eq",
      value: statusFilter,
    });
  }

  // coreBOS portal session handles data scoping automatically
  const { data, isLoading } = useList({
    resource: "policies",
    pagination: { pageSize: 50 },
    filters,
    sorters: [{ field: "title", order: "asc" }],
  });

  const policies = ((data?.data || []) as any[]).filter((p: any) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.title || "").toLowerCase().includes(q) ||
      (p.policy_type || "").toLowerCase().includes(q) ||
      (p.description || "").toLowerCase().includes(q)
    );
  });

  const documents: VaultDocItem[] = policies.map((p: any) => ({
    id: p.id,
    category: p.policy_type || "Policy",
    title: p.title || "Untitled",
    date: p.createdtime
      ? new Date(p.createdtime).toLocaleDateString("en-US", {
          month: "short",
          day: "2-digit",
          year: "numeric",
        })
      : "—",
    status: mapPolicyStatus(p.policystatus),
  }));

  const handleSelect = (id: string) => {
    navigate(`/digibox/policies/${id}`);
  };

  return (
    <>
      <VaultSidebar
        documents={documents}
        onSelect={handleSelect}
        loading={isLoading}
      />

      <main className="vault-panel vault-main-content">
        <div className="vault-hub-content">
          <div className="vault-hub-hero">
            <h1 className="vault-heading-display">POLICIES</h1>
          </div>

          {/* Filter bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--vault-spacing-md)",
              paddingBottom: "var(--vault-spacing-md)",
              borderBottom: "1px solid var(--vault-grid-line)",
              flexWrap: "wrap",
            }}
          >
            <div style={{ display: "flex", gap: "var(--vault-spacing-sm)" }}>
              {FILTER_OPTIONS.map((opt) => (
                <span
                  key={opt.key}
                  className={`vault-nav-link ${statusFilter === opt.key ? "active" : ""}`}
                  onClick={() => setStatusFilter(opt.key)}
                >
                  {opt.label}
                </span>
              ))}
            </div>
            <input
              className="vault-modal-input"
              placeholder="Search policies..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                maxWidth: 280,
                padding: "6px 12px",
                fontSize: "var(--vault-text-small)",
              }}
            />
          </div>

          {/* Stats */}
          <div>
            <span className="vault-label-tiny">
              {policies.length} document{policies.length !== 1 ? "s" : ""}{" "}
              {statusFilter !== "all" ? `(${statusFilter})` : ""}
            </span>
          </div>

          {/* Instructions */}
          <div>
            <p
              className="vault-text-body-editorial"
              style={{ color: "var(--vault-fg-muted)" }}
            >
              Select a policy from the sidebar to view its full content and
              complete any required acceptance actions.
            </p>
          </div>
        </div>
      </main>
    </>
  );
};
